const picluMobileHero = window.matchMedia('(max-width: 749px)');
const picluReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

function initializePicluHeroVideos() {
  for (const hero of document.querySelectorAll('.piclu-hero')) {
    const video = hero.querySelector('[data-piclu-hero-video]');
    if (!video) continue;
    const src = picluMobileHero.matches ? video.dataset.mobileSrc : video.dataset.desktopSrc;
    if (!src || video.getAttribute('src') === src) continue;

    video.pause();
    video.classList.remove('is-visible');
    video.addEventListener('loadeddata', () => video.classList.add('is-visible'), { once: true });
    video.src = src;
    video.load();
    if (video.readyState >= 2) video.classList.add('is-visible');
    video.muted = true;

    if (!picluReducedMotion.matches) video.play().catch(() => {});
  }
}

function initializePicluHeroInteractions() {
  for (const hero of document.querySelectorAll('.piclu-hero')) {
    if (hero.dataset.picluInteractionsReady) continue;
    hero.dataset.picluInteractionsReady = 'true';

    let animationFrame = 0;
    let nextPosition = null;

    const follow = (x, y) => {
      if (picluReducedMotion.matches) return;
      nextPosition = { x, y };
      if (animationFrame) return;
      animationFrame = requestAnimationFrame(() => {
        const bounds = hero.getBoundingClientRect();
        const horizontal = Math.max(-1, Math.min(1, (nextPosition.x - bounds.left - bounds.width / 2) / (bounds.width / 2)));
        const vertical = Math.max(-1, Math.min(1, (nextPosition.y - bounds.top - bounds.height / 2) / (bounds.height / 2)));
        hero.style.setProperty('--piclu-text-x', `${(horizontal * 5).toFixed(1)}px`);
        hero.style.setProperty('--piclu-text-y', `${(vertical * 4).toFixed(1)}px`);
        animationFrame = 0;
      });
    };

    const reset = () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      hero.style.removeProperty('--piclu-text-x');
      hero.style.removeProperty('--piclu-text-y');
    };

    hero.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'touch') follow(event.clientX, event.clientY);
    }, { passive: true });
    hero.addEventListener('pointerleave', reset);
    hero.addEventListener('touchmove', (event) => {
      if (event.touches[0]) follow(event.touches[0].clientX, event.touches[0].clientY);
    }, { passive: true });
    hero.addEventListener('touchend', reset, { passive: true });
    hero.addEventListener('touchcancel', reset, { passive: true });
  }
}

function initializePicluGuarantees() {
  for (const section of document.querySelectorAll('.piclu-guarantees')) {
    if (section.dataset.picluTrustReady) continue;
    section.dataset.picluTrustReady = 'true';

    if (!picluReducedMotion.matches && !window.Shopify?.designMode && 'IntersectionObserver' in window) {
      section.classList.add('is-motion-ready');
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        section.classList.add('is-visible');
        observer.disconnect();
      }, { threshold: 0.15 });
      observer.observe(section);
    } else {
      section.classList.add('is-visible');
    }

    const grid = section.querySelector('.piclu-guarantees__grid');
    if (grid) {
      let tiltFrame = 0;
      let lastPointer = null;
      let activeTouchId = null;

      const resetTilt = (event) => {
        if (event?.pointerType === 'touch' && event.pointerId !== activeTouchId) return;
        if (event?.pointerType === 'touch') activeTouchId = null;
        if (tiltFrame) cancelAnimationFrame(tiltFrame);
        tiltFrame = 0;
        lastPointer = null;
        grid.classList.remove('is-tilting', 'is-touching');
        for (const name of ['--piclu-tilt-x', '--piclu-tilt-y', '--piclu-glow-x', '--piclu-glow-y']) {
          grid.style.removeProperty(name);
        }
      };

      const tiltTowardPointer = (event) => {
        if (picluReducedMotion.matches) return;
        if (event.pointerType === 'touch' && event.pointerId !== activeTouchId) return;
        if (event.pointerType !== 'touch' && activeTouchId !== null) return;
        lastPointer = { x: event.clientX, y: event.clientY, touch: event.pointerType === 'touch' };
        grid.classList.add('is-tilting');
        if (tiltFrame) return;
        tiltFrame = requestAnimationFrame(() => {
          const bounds = grid.getBoundingClientRect();
          const x = Math.max(0, Math.min(1, (lastPointer.x - bounds.left) / bounds.width));
          const y = Math.max(0, Math.min(1, (lastPointer.y - bounds.top) / bounds.height));
          const angle = lastPointer.touch ? 1 : 1.8;
          grid.style.setProperty('--piclu-tilt-x', `${((.5 - y) * angle * 2).toFixed(2)}deg`);
          grid.style.setProperty('--piclu-tilt-y', `${((x - .5) * angle * 2).toFixed(2)}deg`);
          grid.style.setProperty('--piclu-glow-x', `${Math.round(x * 100)}%`);
          grid.style.setProperty('--piclu-glow-y', `${Math.round(y * 100)}%`);
          tiltFrame = 0;
        });
      };

      grid.addEventListener('pointermove', tiltTowardPointer, { passive: true });
      grid.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'touch') resetTilt(event);
      });
      grid.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || picluReducedMotion.matches) return;
        activeTouchId = event.pointerId;
        grid.classList.add('is-touching');
        tiltTowardPointer(event);
      }, { passive: true });
      grid.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'touch') resetTilt(event);
      }, { passive: true });
      grid.addEventListener('pointercancel', (event) => {
        if (event.pointerType === 'touch') resetTilt(event);
      }, { passive: true });
    }

    for (const item of section.querySelectorAll('.piclu-guarantee')) {
      let animationFrame = 0;
      let lastPointer = null;
      let pressedTimer = 0;

      item.addEventListener('pointermove', (event) => {
        if (picluReducedMotion.matches || event.pointerType === 'touch') return;
        lastPointer = { x: event.clientX, y: event.clientY };
        if (animationFrame) return;
        animationFrame = requestAnimationFrame(() => {
          const bounds = item.getBoundingClientRect();
          item.style.setProperty('--piclu-spot-x', `${lastPointer.x - bounds.left}px`);
          item.style.setProperty('--piclu-spot-y', `${lastPointer.y - bounds.top}px`);
          animationFrame = 0;
        });
      }, { passive: true });

      item.addEventListener('pointerleave', () => {
        if (animationFrame) cancelAnimationFrame(animationFrame);
        animationFrame = 0;
        item.style.removeProperty('--piclu-spot-x');
        item.style.removeProperty('--piclu-spot-y');
      });

      item.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || picluReducedMotion.matches) return;
        clearTimeout(pressedTimer);
        item.classList.add('is-pressed');
      }, { passive: true });
      item.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'touch') return;
        pressedTimer = window.setTimeout(() => item.classList.remove('is-pressed'), 380);
      }, { passive: true });
      item.addEventListener('pointercancel', () => {
        clearTimeout(pressedTimer);
        item.classList.remove('is-pressed');
      }, { passive: true });
    }
  }
}

function initializePicluNewsletter() {
  for (const panel of document.querySelectorAll('.piclu-newsletter__inner')) {
    if (panel.dataset.picluNewsletterReady) continue;
    panel.dataset.picluNewsletterReady = 'true';

    let motionFrame = 0;
    let lastPointer = null;
    let activeTouchId = null;

    const reset = (event) => {
      if (event?.pointerType === 'touch' && event.pointerId !== activeTouchId) return;
      if (event?.pointerType === 'touch') activeTouchId = null;
      if (motionFrame) cancelAnimationFrame(motionFrame);
      motionFrame = 0;
      lastPointer = null;
      panel.classList.remove('is-tilting', 'is-touching');
      for (const name of ['--piclu-newsletter-x', '--piclu-newsletter-y', '--piclu-newsletter-light-x', '--piclu-newsletter-light-y']) {
        panel.style.removeProperty(name);
      }
    };

    const follow = (event) => {
      if (picluReducedMotion.matches) return;
      if (event.pointerType === 'touch' && event.pointerId !== activeTouchId) return;
      if (event.pointerType !== 'touch' && activeTouchId !== null) return;
      if (panel.contains(document.activeElement) && document.activeElement.matches('input, button, a')) {
        reset();
        return;
      }

      lastPointer = { x: event.clientX, y: event.clientY, touch: event.pointerType === 'touch' };
      panel.classList.add('is-tilting');
      if (motionFrame) return;
      motionFrame = requestAnimationFrame(() => {
        const bounds = panel.getBoundingClientRect();
        const x = Math.max(0, Math.min(1, (lastPointer.x - bounds.left) / bounds.width));
        const y = Math.max(0, Math.min(1, (lastPointer.y - bounds.top) / bounds.height));
        const angle = lastPointer.touch ? .75 : 1.35;
        panel.style.setProperty('--piclu-newsletter-x', `${((.5 - y) * 2 * angle).toFixed(2)}deg`);
        panel.style.setProperty('--piclu-newsletter-y', `${((x - .5) * 2 * angle).toFixed(2)}deg`);
        panel.style.setProperty('--piclu-newsletter-light-x', `${Math.round(x * 100)}%`);
        panel.style.setProperty('--piclu-newsletter-light-y', `${Math.round(y * 100)}%`);
        motionFrame = 0;
      });
    };

    panel.addEventListener('pointermove', follow, { passive: true });
    panel.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'touch') reset();
    });
    panel.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || picluReducedMotion.matches || event.target.closest('form')) return;
      activeTouchId = event.pointerId;
      panel.classList.add('is-touching');
      follow(event);
    }, { passive: true });
    panel.addEventListener('pointerup', (event) => {
      if (event.pointerType === 'touch') reset(event);
    }, { passive: true });
    panel.addEventListener('pointercancel', (event) => {
      if (event.pointerType === 'touch') reset(event);
    }, { passive: true });
    panel.addEventListener('focusin', () => reset());
  }
}

function initializePicluHero() {
  initializePicluHeroVideos();
  initializePicluHeroInteractions();
  initializePicluGuarantees();
  initializePicluNewsletter();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePicluHero, { once: true });
} else {
  initializePicluHero();
}
picluMobileHero.addEventListener('change', initializePicluHero);
document.addEventListener('shopify:section:load', initializePicluHero);

document.addEventListener('click', (event) => {
  const tab = event.target.closest('[data-piclu-tab]');
  if (tab) {
    const tabs = tab.closest('[data-piclu-tabs]');
    const section = tabs?.closest('.piclu-discover');
    if (!section) return;
    const selected = tab.dataset.picluTab;
    for (const button of tabs.querySelectorAll('[data-piclu-tab]')) {
      const active = button.dataset.picluTab === selected;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    }
    for (const panel of section.querySelectorAll('[data-piclu-panel]')) {
      panel.hidden = panel.dataset.picluPanel !== selected;
    }
  }
});

document.addEventListener('keydown', (event) => {
  const current = event.target.closest('[data-piclu-tab]');
  if (!current || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const buttons = [...current.closest('[data-piclu-tabs]').querySelectorAll('[data-piclu-tab]')];
  const step = event.key === 'ArrowRight' ? 1 : -1;
  const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (buttons.indexOf(current) + step + buttons.length) % buttons.length;
  event.preventDefault();
  buttons[index].focus();
  buttons[index].click();
});
